import {
    checkHoursPassed,
    checkIfBatchAllowsNotify,
    checkIfBatchIncludesIbos,
    isDeviceAllowed,
    processDownloadQueue,
    resetFirstElementInDownloadQueue,
} from "JS/Helpers";
import { useAuth } from "JS/React/Context/AuthenticationProvider";
import {
    useGlobalMediaEssentials,
    useMediaEssentials,
} from "JS/React/Hooks/MediaEssentials";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useVerifyAwsKeys } from "JS/React/Hooks/Media";
import { useRouting } from "JS/React/Hooks/Routes";
import { useCallback, useEffect } from "react";
import { setIsRedirectedToAmmwayConsentForm } from "JS/Helpers/LocalStorageHelpers";
import { useGetLatestUsername } from "JS/React/Hooks/Users";
import { useResumeData } from "JS/React/Hooks/Firebase/Resume";
import {
    useGlobalBlackRecommendations,
    useGlobalRecommendations,
    useOpenAutoPopup,
    useRecommendations,
    useRecommendationsBatch,
} from "JS/React/Hooks/Recommendations";
import { useOpenConsentForm } from "JS/React/Hooks/ConsentForm";
import { RecommendationsState } from "JS/Redux/Recommendations";
import {
    EventActions,
    EventCategories,
    EventNames,
    RecommendationBatch,
} from "JS/Models";
import { useBundleOptIn } from "../Hooks/BundleOptIn";
import { useAppSelector } from "JS/Redux/Store";
import { useBundleSubscription } from "../Hooks/BundleSubscription";
import { useVersionUpdate } from "../Hooks/VersionUpdate";

import { usePurchaseBundlePopup } from "../Hooks/PurchaseBundlePopup";
import { useFirebaseLogger } from "../Hooks/Firebase";

export const ForegroundTasks = () => {
    const { fetch: fetchVersionUpdate } = useVersionUpdate();
    const {
        categoryId,
        linkProvider,
        history,
        allowRedirect,
        isAuthenticated,
        deviceAllowed,
    } = useRoutingVariables();
    const { fetchMediaEssentials, mediaEssentials } =
        useMediaEssentialVariables();
    const {
        fetchBatch,
        fetchRecommendations,
        recommendationState,
        refetchBatch,
        openPopup,
    } = useRecommendationVariables(categoryId);
    useMount(recommendationState, refetchBatch, fetchRecommendations);
    const { verifyAwsKeys } = useVerifyAwsKeys();
    const { syncUsername } = useGetLatestUsername();
    const {
        logAudioResume,
        logVideoResume,
        logAudioBundleResume,
        logPlaylistResume,
    } = useResumeData();
    const { openForm } = useOpenConsentForm();
    const { showPurchaseBundlePopup } = useAppSelector(
        (state) => state.purchaseBundle,
    );
    const { showBundleOptInPopup } = useAppSelector(
        (state) => state.bundleOptIn,
    );
    const { showBundleSubscriptionPopup } = useAppSelector(
        (state) => state.bundleSubscription,
    );
    const { getBundleOptInStatus } = useBundleOptIn(true);
    const { getBundleSubscriptionStatus } = useBundleSubscription(true);
    const { getPurchaseBundleStatus } = usePurchaseBundlePopup(true);
    const { logFirebaseEvent } = useFirebaseLogger();

    const onVisibilityChange = useCallback(async () => {
        if (
            document.visibilityState === "visible" &&
            deviceAllowed &&
            isAuthenticated
        ) {
            await fetchVersionUpdate();
            logFirebaseEvent(EventNames.APPLICATION_LAUNCHED, {
                action: EventActions.APPLICATION_LAUNCHED,
                category: EventCategories.GENERAL,
            });
            verifyAwsKeys().then(async () => {
                const mediaEssentialResp = await fetchMediaEssentials();
                if (!allowRedirect && mediaEssentialResp?.has_event_started)
                    history.push(linkProvider.react.webcast().index(true));
                if (!mediaEssentials?.has_event_started && !allowRedirect) {
                    openForm();

                    if (
                        recommendationState?.batch &&
                        checkHoursPassed(
                            recommendationState?.lastFetched,
                            HOURS_TO_CHECK,
                        )
                    )
                        await fetchBatch();
                    else openPopup(categoryId);

                    if (!showPurchaseBundlePopup)
                        await getPurchaseBundleStatus();
                    if (!showBundleOptInPopup) getBundleOptInStatus();
                    if (!showBundleSubscriptionPopup)
                        getBundleSubscriptionStatus();
                }
                resetFirstElementInDownloadQueue();
                processDownloadQueue();
                setIsRedirectedToAmmwayConsentForm("false");
                syncUsername();
                logAudioResume();
                logVideoResume();
                logAudioBundleResume();
                logPlaylistResume();
            });
        }
    }, [
        allowRedirect,
        deviceAllowed,
        fetchBatch,
        fetchMediaEssentials,
        history,
        isAuthenticated,
        linkProvider.react,
        logAudioBundleResume,
        logAudioResume,
        logPlaylistResume,
        logVideoResume,
        mediaEssentials?.has_event_started,
        openForm,
        getPurchaseBundleStatus,
        getBundleOptInStatus,
        getBundleSubscriptionStatus,
        openPopup,
        recommendationState?.batch,
        recommendationState?.lastFetched,
        syncUsername,
        verifyAwsKeys,
        categoryId,
    ]);

    useEffect(() => {
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () =>
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
    }, [onVisibilityChange]);

    return <></>;
};

const HOURS_TO_CHECK = 2;

const useFirstStart = (refetchBatch: () => void) => {
    const { blackRecommendationState } = useGlobalBlackRecommendations();
    const { recommendationState } = useGlobalRecommendations();

    useEffect(() => {
        if (blackRecommendationState?.firstStart) {
            /**Anything here will run on application restart */
            if (
                recommendationState?.batch &&
                checkHoursPassed(
                    recommendationState?.lastFetched,
                    HOURS_TO_CHECK,
                )
            )
                refetchBatch();
        }
    }, [blackRecommendationState?.firstStart]);
};

const useRoutingVariables = () => {
    const params: any = useParams();
    const { title, categoryId } = params;
    const { linkProvider } = useRouting();
    const history = useHistory();
    const location = useLocation();
    const allowRedirect =
        location.pathname === linkProvider.react.webcast().index() ||
        location.pathname === linkProvider.react.webcast().detail(title) ||
        location.pathname === linkProvider.react.webcast().stream(title);
    const { isAuthenticated } = useAuth();
    const deviceAllowed = isDeviceAllowed();

    return {
        categoryId,
        linkProvider,
        history,
        allowRedirect,
        isAuthenticated,
        deviceAllowed,
    };
};

const useMediaEssentialVariables = () => {
    const { refetch: fetchMediaEssentials } = useMediaEssentials(true);
    const { mediaEssentials } = useGlobalMediaEssentials();

    return {
        fetchMediaEssentials,
        mediaEssentials,
    };
};

const useRecommendationVariables = (categoryId: string) => {
    const { recommendationState, setGlobalRecommendations } =
        useGlobalRecommendations();
    const { refetch: refetchBatch } = useRecommendationsBatch();
    const { refetch: refetchRecommendations } = useRecommendations(true);
    const { openPopup } = useOpenAutoPopup();
    const fetchRecommendations = useCallback(
        () =>
            refetchRecommendations().then(() => {
                setGlobalRecommendations({
                    noOfPopups: 0,
                });

                openPopup(categoryId, true);
            }),
        [
            categoryId,
            openPopup,
            refetchRecommendations,
            setGlobalRecommendations,
        ],
    );
    const fetchBatch = useCallback(async () => {
        try {
            const apiBatch = await refetchBatch();
            const localBatch = recommendationState?.batch;

            if (localBatch) {
                const batchChanged = localBatch?.batchId !== apiBatch?.batchId;
                const includesIbo = checkIfBatchIncludesIbos(apiBatch);
                const allowsNotify = checkIfBatchAllowsNotify(apiBatch);

                if (batchChanged && includesIbo && allowsNotify)
                    await fetchRecommendations();
            }
        } catch {
            /*do nothing*/
        }
    }, [refetchBatch, recommendationState?.batch, fetchRecommendations]);

    useFirstStart(fetchBatch);

    return {
        fetchBatch,
        fetchRecommendations,
        recommendationState,
        refetchBatch,
        openPopup,
    };
};

const useMount = (
    recommendationState: RecommendationsState,
    refetchBatch: () => Promise<RecommendationBatch>,
    fetchRecommendations: () => Promise<void>,
) => {
    const onMount = async () => {
        const timePassed = recommendationState?.lastFetched
            ? checkHoursPassed(recommendationState?.lastFetched, HOURS_TO_CHECK)
            : true;

        if (!recommendationState.batch && timePassed) {
            const apiBatch = await refetchBatch();
            const includesIbo = checkIfBatchIncludesIbos(apiBatch);
            const allowsNotify = checkIfBatchAllowsNotify(apiBatch);

            if (
                !recommendationState.recommendations &&
                includesIbo &&
                allowsNotify
            )
                await fetchRecommendations();
        }
    };

    useEffect(() => {
        onMount();
    }, []);
};
